import { makeAutoObservable } from "mobx";
import { Approval, ApprovalAuditRequest } from "../../models/Approval";
import { ApprovalApi } from "../../apis/Approval";

export class ApprovalManagementPageModel {
    unprocessedApprovals: Approval[] = [];
    processingApprovals: Approval[] = [];
    processedApprovals: Approval[] = [];
    loading = false;
    selectedApproval: Approval | null = null;
    showConfirmDialog = false;
    showRefuseDialog = false;
    refuseReason = "";
    currentTab: "unprocessed" | "processing" | "processed" = "unprocessed";
    examFee = "0";
    showExamFeeDialog = false;
    savingExamFee = false;

    constructor() {
        makeAutoObservable(this);
    }

    async loadExamFee() {
        try {
            this.examFee = await ApprovalApi.getExamFee();
        } catch (error) {
            console.error("加载笔试费用失败:", error);
            this.examFee = "0";
        }
    }

    setExamFee(value: string) {
        this.examFee = value;
    }

    setShowExamFeeDialog(show: boolean) {
        this.showExamFeeDialog = show;
    }

    async saveExamFee() {
        const fee = this.examFee.trim();
        if (!/^\d+(\.\d{1,2})?$/.test(fee)) {
            return false;
        }
        this.savingExamFee = true;
        try {
            const success = await ApprovalApi.setExamFee(fee);
            if (success) {
                this.examFee = fee;
                this.showExamFeeDialog = false;
            }
            return success;
        } catch (error) {
            console.error("保存笔试费用失败:", error);
            return false;
        } finally {
            this.savingExamFee = false;
        }
    }

    async loadUnprocessed() {
        this.loading = true;
        try {
            this.unprocessedApprovals = await ApprovalApi.getAllUnprocessed();
        } catch (error) {
            console.error("加载待审核记录失败:", error);
        } finally {
            this.loading = false;
        }
    }

    async loadProcessing() {
        this.loading = true;
        try {
            this.processingApprovals = await ApprovalApi.getAllProcessing();
        } catch (error) {
            console.error("加载审核中记录失败:", error);
        } finally {
            this.loading = false;
        }
    }

    async loadProcessed() {
        this.loading = true;
        try {
            this.processedApprovals = await ApprovalApi.getAllProcessed();
        } catch (error) {
            console.error("加载已审核记录失败:", error);
        } finally {
            this.loading = false;
        }
    }

    setSelectedApproval(approval: Approval | null) {
        this.selectedApproval = approval;
    }

    setShowConfirmDialog(show: boolean) {
        this.showConfirmDialog = show;
    }

    setShowRefuseDialog(show: boolean) {
        this.showRefuseDialog = show;
    }

    setRefuseReason(reason: string) {
        this.refuseReason = reason;
    }

    setCurrentTab(tab: "unprocessed" | "processing" | "processed") {
        this.currentTab = tab;
    }

    async approveAudit() {
        if (!this.selectedApproval) return;

        try {
            const params: ApprovalAuditRequest = {
                id: this.selectedApproval.id,
                approved: true
            };

            const success = await ApprovalApi.audit(params);
            if (success) {
                if (this.currentTab === "unprocessed") {
                    await this.loadUnprocessed();
                } else if (this.currentTab === "processing") {
                    await this.loadProcessing();
                }
                await this.loadProcessed();

                this.setSelectedApproval(null);
                this.setShowConfirmDialog(false);
            }
        } catch (error) {
            console.error("审核通过失败:", error);
        }
    }

    async refuseAudit() {
        if (!this.selectedApproval || !this.refuseReason) return;

        try {
            const params: ApprovalAuditRequest = {
                id: this.selectedApproval.id,
                approved: false,
                refuseReason: this.refuseReason
            };

            const success = await ApprovalApi.audit(params);
            if (success) {
                if (this.currentTab === "unprocessed") {
                    await this.loadUnprocessed();
                } else if (this.currentTab === "processing") {
                    await this.loadProcessing();
                }
                await this.loadProcessed();

                this.setSelectedApproval(null);
                this.setShowRefuseDialog(false);
                this.setRefuseReason("");
            }
        } catch (error) {
            console.error("审核拒绝失败:", error);
        }
    }
}
